// SCR-STAFF-306 — لیست انتظار (US-SCH-050، 051؛ BR-SCH-050 تا 052): ورودی‌ها با ترجیحات، اولویت و انقضا (حداکثر ۶۰ روز)، ثبت و لغو،
// پیشنهاد جاری (زمان آزادشده با مهلت پاسخ ۳۰ یا ۱۰ دقیقه) و پذیرش یا رد تلفنی به نمایندگی از مراجعه‌کننده.
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BookingServiceOption, type WaitlistEntryView, type WaitlistStatus } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { PatientPicker, type BookingPatient } from './BookingDialog';
import { addDays, formatDay, formatTime, IRANIAN_WEEK, schKeys, useActiveBranch, useBookingOptions, useServiceSearch } from './common';

export const SCR_STAFF_306 = 'SCR-STAFF-306';

const STATUSES: readonly WaitlistStatus[] = ['Active', 'Offered', 'Fulfilled', 'Expired', 'Cancelled'];

/** حداکثر اعتبار ورودی (BR-SCH-050). */
const MAX_DAYS = 60;

interface Draft {
  patient: BookingPatient | null;
  serviceId: string;
  providerId: string;
  dateFrom: string;
  dateTo: string;
  weekdays: number[];
  timeFrom: string;
  timeTo: string;
  priority: number;
  notes: string;
}

function emptyDraft(today: string | null): Draft {
  return {
    patient: null,
    serviceId: '',
    providerId: '',
    dateFrom: formatJalaliInput(today),
    dateTo: formatJalaliInput(today ? addDays(today, 14) : null),
    weekdays: [],
    timeFrom: '',
    timeTo: '',
    priority: 1,
    notes: '',
  };
}

/** فرم ثبت ورودی (AC-SCH-050.1). */
function RegisterDialog({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('sch');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId, null, open);
  const serviceSearch = useServiceSearch(branchId, open);
  const today = options.data?.today ?? null;
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(today));
  const [touched, setTouched] = useState(false);
  const patientInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open && today) {
      setDraft((current) => (current.dateFrom ? current : emptyDraft(today)));
    }
  }, [open, today]);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const providers = useBookingOptions(branchId, draft.serviceId || null, open && draft.serviceId !== '').data?.providers ?? [];
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/waitlist', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            patientId: draft.patient?.id ?? null,
            serviceId: draft.serviceId || null,
            providerId: draft.providerId || null,
            branchId,
            dateFrom: parseJalaliInput(draft.dateFrom) ?? null,
            dateTo: parseJalaliInput(draft.dateTo) ?? null,
            weekdays: draft.weekdays,
            timeFrom: draft.timeFrom || null,
            timeTo: draft.timeTo || null,
            priority: draft.priority,
            expiresOn: null,
            notes: draft.notes.trim() || null,
          },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setDraft(emptyDraft(today));
      setTouched(false);
      onSaved();
    },
  });
  const valid = draft.patient !== null && draft.serviceId !== '' && parseJalaliInput(draft.dateTo) !== null;
  return (
    <FormDialog
      open={open}
      titleKey="sch:waitlist.register"
      screenId={SCR_STAFF_306}
      onSubmit={() => {
        setTouched(true);
        if (valid) {
          save.mutate();
        }
      }}
      onClose={onClose}
      isDirty={draft.patient !== null || draft.serviceId !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <PatientPicker value={draft.patient} onChange={(value) => set('patient', value)} disabled={false} inputRef={patientInput} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Autocomplete<BookingServiceOption, false, false, false>
          value={serviceSearch.find(draft.serviceId) ?? null}
          onChange={(_event, next) => setDraft((current) => ({ ...current, serviceId: next?.id ?? '', providerId: '' }))}
          options={serviceSearch.services}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(items) => items}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              serviceSearch.search(value);
            }
          }}
          loading={serviceSearch.isFetching}
          noOptionsText={t('sch:booking.service_none')}
          sx={{ flex: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label={t('sch:booking.service')}
              error={touched && draft.serviceId === ''}
              inputProps={{ ...params.inputProps, 'data-testid': 'waitlist-service' }}
            />
          )}
        />
        <TextField
          select
          label={t('sch:booking.provider')}
          value={draft.providerId}
          onChange={(event) => set('providerId', event.target.value)}
          sx={{ flex: 1 }}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">{t('sch:booking.provider_any')}</MenuItem>
          {providers.map((provider) => (
            <MenuItem key={provider.id} value={provider.id}>
              {provider.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('sch:waitlist.date_from')}
          value={draft.dateFrom}
          onChange={(event) => set('dateFrom', event.target.value)}
          placeholder={t('sch:schedules.date_placeholder')}
          inputProps={{ dir: 'ltr', 'data-testid': 'waitlist-date-from' }}
        />
        <TextField
          required
          label={t('sch:waitlist.date_to')}
          value={draft.dateTo}
          error={touched && parseJalaliInput(draft.dateTo) === null}
          helperText={t('sch:waitlist.max_days', { days: faDigits(MAX_DAYS) })}
          onChange={(event) => set('dateTo', event.target.value)}
          placeholder={t('sch:schedules.date_placeholder')}
          inputProps={{ dir: 'ltr', 'data-testid': 'waitlist-date-to' }}
        />
        <TextField
          select
          label={t('sch:waitlist.priority')}
          value={draft.priority}
          onChange={(event) => set('priority', Number(event.target.value))}
          sx={{ minWidth: 140 }}
        >
          {[1, 2, 3].map((value) => (
            <MenuItem key={value} value={value}>
              {t(`sch:waitlist.priority_${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }} role="group" aria-label={t('sch:waitlist.weekdays')}>
        {IRANIAN_WEEK.map((day) => {
          const active = draft.weekdays.includes(day);
          return (
            <Chip
              key={day}
              label={t(`sch:weekday.${day}`)}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              aria-pressed={active}
              onClick={() => set('weekdays', active ? draft.weekdays.filter((value) => value !== day) : [...draft.weekdays, day])}
            />
          );
        })}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('sch:waitlist.time_from')} value={draft.timeFrom} onChange={(event) => set('timeFrom', event.target.value)} placeholder="09:00" inputProps={{ dir: 'ltr' }} />
        <TextField label={t('sch:waitlist.time_to')} value={draft.timeTo} onChange={(event) => set('timeTo', event.target.value)} placeholder="13:00" inputProps={{ dir: 'ltr' }} />
        <TextField label={t('sch:booking.notes')} value={draft.notes} onChange={(event) => set('notes', event.target.value)} inputProps={{ maxLength: 500 }} sx={{ flex: 1 }} />
      </Stack>
    </FormDialog>
  );
}

export function WaitlistPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { branchId } = useActiveBranch();
  const timeZone = useBookingOptions(branchId).data?.timeZone;
  const [status, setStatus] = useState<WaitlistStatus | ''>('');
  const [registering, setRegistering] = useState(false);
  const tenantId = session?.tenantId;
  const list = useQuery({
    queryKey: schKeys.waitlist(tenantId, status),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/waitlist', { params: { query: { status: status || undefined, limit: 100 } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: schKeys.all(tenantId) });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/waitlist/{id}:cancel', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const accept = useMutation({
    mutationFn: (offerId: string) => unwrap(tenantApi.POST('/api/v1/scheduling/waitlist-offers/{id}:accept', { params: { path: { id: offerId } } })),
    onSuccess: refresh,
  });
  const decline = useMutation({
    mutationFn: (offerId: string) => unwrap(tenantApi.POST('/api/v1/scheduling/waitlist-offers/{id}:decline', { params: { path: { id: offerId } } })),
    onSuccess: refresh,
  });
  const full = access.accessMode === 'Full';
  const columns: DataTableColumn<WaitlistEntryView>[] = [
    { id: 'patient', headerKey: 'sch:waitlist.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'service', headerKey: 'sch:waitlist.columns.service', cell: (row) => (row.serviceMasked ? t('sch:day.masked_service') : (row.serviceName ?? '—')) },
    { id: 'provider', headerKey: 'sch:waitlist.columns.provider', cell: (row) => row.providerName ?? t('sch:booking.provider_any') },
    {
      id: 'range',
      headerKey: 'sch:waitlist.columns.range',
      cell: (row) => `${formatDay(row.dateFrom, 'd MMMM')} – ${formatDay(row.dateTo, 'd MMMM')}`,
    },
    { id: 'priority', headerKey: 'sch:waitlist.columns.priority', cell: (row) => t(`sch:waitlist.priority_${Number(row.priority)}`) },
    { id: 'expires', headerKey: 'sch:waitlist.columns.expires', cell: (row) => formatDay(row.expiresOn, 'd MMMM yyyy') },
    {
      id: 'status',
      headerKey: 'sch:waitlist.columns.status',
      cell: (row) => (
        <Stack spacing={0.5}>
          <span data-testid="waitlist-status">{t(`sch:waitlist.status.${row.status}`)}</span>
          {row.status === 'Offered' && row.currentOffer ? (
            <Typography variant="caption" color="text.secondary" data-testid="waitlist-offer">
              {t('sch:waitlist.offer_line', {
                date: formatDay(row.currentOffer.start.slice(0, 10), 'EEEE d MMMM'),
                time: formatTime(row.currentOffer.start, timeZone),
                expires: formatTime(row.currentOffer.expiresAt, timeZone),
              })}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'sch:waitlist.columns.actions',
      cell: (row) =>
        !full ? null : (
          <Stack direction="row" spacing={1}>
            {row.status === 'Offered' && row.currentOffer ? (
              <>
                <Button size="small" variant="contained" onClick={() => accept.mutate(row.currentOffer!.id)} data-testid="waitlist-accept">
                  {t('sch:waitlist.accept')}
                </Button>
                <Button size="small" onClick={() => decline.mutate(row.currentOffer!.id)} data-testid="waitlist-decline">
                  {t('sch:waitlist.decline')}
                </Button>
              </>
            ) : null}
            {row.status === 'Active' || row.status === 'Offered' ? (
              <Button size="small" color="error" onClick={() => cancel.mutate(row.id)} data-testid="waitlist-cancel">
                {t('sch:waitlist.cancel')}
              </Button>
            ) : null}
          </Stack>
        ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_306}
      titleKey="sch:waitlist.title"
      subtitleKey="sch:waitlist.subtitle"
      actions={
        full ? (
          <Button variant="contained" onClick={() => setRegistering(true)} data-testid="waitlist-register">
            {t('sch:waitlist.register')}
          </Button>
        ) : undefined
      }
      filters={
        <TextField
          select
          size="small"
          label={t('sch:waitlist.columns.status')}
          value={status}
          onChange={(event) => setStatus(event.target.value as WaitlistStatus | '')}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('sch:waitlist.all')}</MenuItem>
          {STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`sch:waitlist.status.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      }
    >
      <DataTable
        captionKey="sch:waitlist.title"
        columns={columns}
        rows={list.data?.items}
        getRowId={(row) => row.id}
        isLoading={list.isPending}
        error={list.error ?? cancel.error ?? accept.error ?? decline.error}
        onRetry={() => void list.refetch()}
        emptyTitleKey="sch:waitlist.empty"
      />
      <RegisterDialog
        open={registering}
        onClose={() => setRegistering(false)}
        onSaved={() => {
          setRegistering(false);
          refresh();
        }}
      />
    </ListPage>
  );
}
